import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { ICarePlanDetailed } from 'src/app/interfaces/CarePlan/ICarePlan';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';
import { CarePlanService } from 'src/app/services/carePlan.service';
import { SwalToastService } from 'src/app/services/swal.service';

type CarePlanTableRow = ICarePlanDetailed & {
  periodStartDatetime?: string | null;
  periodEndDatetime?: string | null;
};

@Component({
  selector: 'app-care-plan',
  templateUrl: './care-plan.component.html',
  styleUrls: ['./care-plan.component.css'],
  standalone: false,
})
export class CarePlanComponent {
  private destroy$ = new Subject<void>();

  activeCarePlans?: CarePlanTableRow[];
  pastCarePlans?: CarePlanTableRow[];
  isLoading = false;
  isDeleteModalVisible = false;
  deleteCarePlanId?: number;
  customActions = [
    {
      iconType: 'icons:form',
      tooltip: 'Edit',
      handler: (item: ICarePlanDetailed) => this.openModalFun(item),
      isVisible: (item: ICarePlanDetailed) =>
        (!!item?.isSubmitted || this.isExistingRevision(item)) &&
        !item?.isStopped &&
        this.hasPermission(['a-cp']) &&
        this.isEocOrInpatientContext(),
    },
    {
      iconType: 'eye',
      tooltip: 'View',
      handler: (item: ICarePlanDetailed) => this.openViewFun(item),
      isVisible: (item: ICarePlanDetailed) =>
        !!item?.isSubmitted || this.isExistingRevision(item),
    },
  ];

  constructor(
    private readonly carePlanService: CarePlanService,
    private readonly eocService: EpisodeOfCareService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService,
    private readonly router: Router
  ) {}

  ngOnInit() {
    this.carePlanService.carePlanLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    this.carePlanService.activeCarePlanDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ICarePlanDetailed[]) => {
        this.activeCarePlans = this.mapCarePlanTableRows(data);
      });

    this.carePlanService.pastCarePlanDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ICarePlanDetailed[]) => {
        this.pastCarePlans = this.mapCarePlanTableRows(data);
      });

    this.carePlanService.getData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openAddCarePlan = () => {
    this.carePlanService.carePlanReadOnlySub.next(false);
    this.carePlanService.carePlanEditSub.next(undefined);
    this.carePlanService.carePlanModalSub.next(true);
  };

  openModalFun = (item?: ICarePlanDetailed) => {
    this.carePlanService.carePlanReadOnlySub.next(false);
    this.carePlanService.carePlanEditSub.next(item);
    this.carePlanService.carePlanModalSub.next(true);
  };

  openViewFun = (item: ICarePlanDetailed) => {
    this.carePlanService.carePlanReadOnlySub.next(true);
    this.carePlanService.carePlanEditSub.next(item);
    this.carePlanService.carePlanModalSub.next(true);
  };

  deleteFun = (carePlanId: number) => {
    this.deleteCarePlanId = carePlanId;
    this.isDeleteModalVisible = true;
  };

  closeDeleteModal() {
    this.isDeleteModalVisible = false;
    this.deleteCarePlanId = undefined;
  }

  confirmDeleteCarePlan(deleteLinkedDraftRecords: boolean) {
    if (!this.deleteCarePlanId) {
      this.closeDeleteModal();
      return;
    }

    this.carePlanService
      .deleteCarePlan(this.deleteCarePlanId, deleteLinkedDraftRecords)
      .subscribe({
        next: (response) => {
          this.closeDeleteModal();
          this.swalToastService.toastSuccess(
            response.message || 'Care Plan deleted successfully',
          );
          this.carePlanService.carePlanActivitiesSub.next(null);
          this.carePlanService.getData();
          this.carePlanService.notifyCarePlanChanged();
        },
        error: (err) => {
          this.swalToastService.toastError(
            err?.error?.message || 'Unable to delete care plan',
          );
        },
      });
  }

  stopFun = (carePlanId: number) => {
    const visitId = this.eocService.currentVisitId();

    if (!visitId) {
      this.swalToastService.toastError('Unable to stop care plan');
      return;
    }

    Swal.fire({
      text: `${this.translate.instant(
        'stopPlaceholder2'
      )} ${this.translate.instant('Care Plan')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.carePlanService.stopCarePlan(carePlanId, visitId).subscribe({
          next: (response) => {
            this.swalToastService.toastSuccess(
              response.message || 'Care Plan stopped successfully'
            );
            this.carePlanService.getData();
            this.carePlanService.notifyCarePlanChanged();
          },
          error: (err) => {
            this.swalToastService.toastError(
              err?.error?.message || 'Unable to stop care plan'
            );
          },
        });
      }
    });
  };

  revertStopFun = (carePlanId: number) => {
    Swal.fire({
      text: `${this.translate.instant(
        'revertPlaceholder2'
      )} ${this.translate.instant('Care Plan')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.carePlanService.reapplyCarePlan(carePlanId).subscribe({
          next: (response) => {
            this.swalToastService.toastSuccess(
              response.message || 'Care Plan reverted successfully'
            );
            this.carePlanService.getData();
            this.carePlanService.notifyCarePlanChanged();
          },
          error: (err) => {
            this.swalToastService.toastError(
              err?.error?.message || 'Unable to revert care plan'
            );
          },
        });
      }
    });
  };

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  isEocOrInpatientContext(): boolean {
    const hasEoc = !(
      this.eocService.currentEocId() === null ||
      this.eocService.currentEocId() === ''
    );

    return (
      hasEoc &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  isExistingRevision = (item: ICarePlanDetailed): boolean =>
    !item?.isSubmitted && !!item?.replacementId && !item?.isStopped;

  private mapCarePlanTableRows(data: ICarePlanDetailed[]): CarePlanTableRow[] {
    return (data ?? []).map((item) => ({
      ...item,
      periodStartDatetime: item.periodStart,
      periodEndDatetime: item.periodEnd,
    }));
  }
}
